import { useState, useCallback, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';

interface RateLimitConfig {
  maxRequests: number;
  windowMs: number;
  message?: string;
  blockDuration?: number; // How long to block after limit exceeded
  progressive?: boolean; // Progressive delays
}

interface RateLimitState {
  requests: number[];
  isBlocked: boolean;
  blockUntil?: number;
  violations: number;
}

const defaultConfig: RateLimitConfig = {
  maxRequests: 10,
  windowMs: 60000, // 1 minute
  blockDuration: 300000, // 5 minutes
  progressive: true,
  message: 'Muitas tentativas. Tente novamente em alguns instantes.'
};

export function useEnhancedRateLimit(config: Partial<RateLimitConfig> = {}) {
  const finalConfig = { ...defaultConfig, ...config };
  const { toast } = useToast();
  
  const [state, setState] = useState<RateLimitState>({
    requests: [],
    isBlocked: false,
    violations: 0
  });

  // Check if currently blocked
  const isCurrentlyBlocked = useCallback(() => {
    if (state.blockUntil && Date.now() < state.blockUntil) {
      return true;
    } else if (state.blockUntil && Date.now() >= state.blockUntil) {
      // Block period expired, reset
      setState(prev => ({
        ...prev,
        isBlocked: false,
        blockUntil: undefined
      }));
    }
    return false;
  }, [state.blockUntil]);

  const checkRateLimit = useCallback(() => {
    if (isCurrentlyBlocked()) {
      const remainingTime = Math.ceil((state.blockUntil! - Date.now()) / 1000);
      toast({
        title: "Bloqueado Temporariamente",
        description: `Aguarde ${remainingTime} segundos antes de tentar novamente.`,
        variant: "destructive",
      });
      return false;
    }

    const now = Date.now();
    const windowStart = now - finalConfig.windowMs;
    
    setState(prevState => {
      // Filter requests within time window
      const recentRequests = prevState.requests.filter(time => time > windowStart);
      
      if (recentRequests.length >= finalConfig.maxRequests) {
        const newViolations = prevState.violations + 1;
        
        // Progressive blocking - longer blocks for repeat violations
        let blockDuration = finalConfig.blockDuration!;
        if (finalConfig.progressive && newViolations > 1) {
          blockDuration = blockDuration * Math.pow(2, newViolations - 1); // Exponential backoff
        }
        
        const blockUntil = now + blockDuration;
        
        toast({
          title: "Limite Excedido",
          description: `${finalConfig.message} Bloqueado por ${Math.ceil(blockDuration / 1000)} segundos.`,
          variant: "destructive",
        });

        // Log security event for monitoring
        console.warn('Rate limit exceeded', {
          violations: newViolations,
          blockDuration,
          timestamp: new Date().toISOString()
        });

        return {
          requests: recentRequests,
          isBlocked: true,
          blockUntil,
          violations: newViolations
        };
      }
      
      return {
        ...prevState,
        requests: recentRequests
      };
    });
    
    return true;
  }, [finalConfig, isCurrentlyBlocked, state.blockUntil, toast]);

  const executeWithRateLimit = useCallback(async <T>(
    asyncFunction: () => Promise<T>,
    identifier?: string
  ): Promise<T | null> => {
    if (!checkRateLimit()) {
      return null;
    }

    // Add new request to history
    setState(prevState => ({
      ...prevState,
      requests: [...prevState.requests, Date.now()]
    }));

    try {
      const result = await asyncFunction();
      
      // Reset violations on successful request
      setState(prev => ({
        ...prev,
        violations: Math.max(0, prev.violations - 0.1) // Slowly reduce violations
      }));
      
      return result;
    } catch (error) {
      console.error('Rate limited function failed:', error);
      throw error;
    }
  }, [checkRateLimit]);

  const reset = useCallback(() => {
    setState({
      requests: [],
      isBlocked: false,
      violations: 0
    });
  }, []);

  // Auto-cleanup old violations
  useEffect(() => {
    const cleanup = setInterval(() => {
      setState(prev => ({
        ...prev,
        violations: Math.max(0, prev.violations - 0.1)
      }));
    }, 60000); // Clean up every minute

    return () => clearInterval(cleanup);
  }, []);

  const getStatus = useCallback(() => {
    const now = Date.now();
    const windowStart = now - finalConfig.windowMs;
    const recentRequests = state.requests.filter(time => time > windowStart);
    
    return {
      remainingRequests: Math.max(0, finalConfig.maxRequests - recentRequests.length),
      resetTime: Math.max(...recentRequests, windowStart) + finalConfig.windowMs,
      isBlocked: isCurrentlyBlocked(),
      violations: state.violations,
      blockUntil: state.blockUntil
    };
  }, [finalConfig, state, isCurrentlyBlocked]);

  return {
    isLimited: isCurrentlyBlocked(),
    executeWithRateLimit,
    checkRateLimit,
    reset,
    getStatus
  };
}